import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Dimensions,
  ActivityIndicator,
  Modal,
  Image,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { fetchUserProfile, handleApiResponse } from '../services/userDataService';
import Constants from "expo-constants";

const { width, height } = Dimensions.get('window');
import { MaskedTextInput } from "react-native-mask-text";
import { API_BASE_URL, AUTH_API_URL } from '../config/api';
import { useCustomAlertModal } from '../components/CustomAlertModal';

export default function EditProfileScreen({ navigation }) {
  const { 
    customAlertModal, 
    showCustomAlert, 
    hideCustomAlert, 
    handleCustomAlertConfirm
  } = useCustomAlertModal();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    birthDate: '',
    placeOfBirth: '',
    nik: '',
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
    profilePicture: null,
  });
  
  const [originalData, setOriginalData] = useState({});
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [profileImageUri, setProfileImageUri] = useState(null);

  const [isEmailEditable, setIsEmailEditable] = useState(true);

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setIsLoadingProfile(true);
      const result = await fetchUserProfile();
      
      if (result.data) {
        const userData = result.data;
        const profileData = {
          fullName: userData.user_fullname || userData.name || '',
          email: userData.user_email || userData.email || '',
          birthDate: userData.user_dob || userData['date-of-birth'] || userData.birthDate || '',
          placeOfBirth: userData.user_identity_pob || userData['ocr-place-dob'] || userData.placeOfBirth || '',
          nik: userData.user_identity_number || userData.nik || '',
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: '',
          profilePicture: userData.user_avatar || null,
        };
        // format phone to remove the prefixes of 08/628/+628
        const userPhone = userData.user_telp || userData.telp || userData.phone || '';
        if(!!userPhone) {
          if(userPhone.startsWith('08')) {
            profileData.phone = userPhone.slice(-1 * (userPhone.length - 2));
          } else if(userPhone.startsWith('628')) {
            profileData.phone = userPhone.slice(-1 * (userPhone.length - 3));
          } else if(userPhone.startsWith('+628')) {
            profileData.phone = userPhone.slice(-1 * (userPhone.length - 4));
          } else {
            profileData.phone = '';
          }
        }
        
        setFormData(profileData);
        setOriginalData(profileData);
        setProfileImageUri(`${API_BASE_URL}/${userData.user_avatar}`);
        setIsEmailEditable(!userData.prevent_change_email);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      showCustomAlert('Error', 'Gagal memuat data profil. Silakan coba lagi.');
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImagePicker = async () => {
    try {
      // Request permission
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();
      
      if (permissionResult.granted === false) {
        showCustomAlert('Permission Required', 'Izin akses galeri diperlukan untuk mengubah foto profil.');
        return;
      }

      // Launch image picker
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
        base64: true,
      });

      if (!result.canceled && result.assets[0]) {
        const asset = result.assets[0];
        setProfileImageUri(asset.uri);
        setFormData(prev => ({
          ...prev,
          profilePicture: {
            uri: asset.uri,
            base64: asset.base64,
            type: 'image/jpeg',
            name: 'profile.jpg'
          }
        }));
      }
    } catch (error) {
      console.error('Error picking image:', error);
      showCustomAlert('Error', 'Gagal memilih gambar. Silakan coba lagi.');
    }
  };

  const validateForm = () => {
    const { fullName, email, phone, birthDate, placeOfBirth, nik, newPassword, confirmNewPassword } = formData;
    
    // Basic validation
    if (!fullName.trim()) {
      showCustomAlert('Error', 'Nama lengkap harus diisi');
      return false;
    }
    
    if (!email.trim() && !phone.trim()) {
      showCustomAlert('Error', 'Email dan nomor HP harus diisi');
      return false;
    }
    
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showCustomAlert('Error', 'Format email tidak valid');
      return false;
    }
    
    if (phone && !/^[0-9+\-\s()]+$/.test(phone)) {
      showCustomAlert('Error', 'Format nomor HP tidak valid');
      return false;
    }

    // Validate phone format to make sure it is valid Indonesian cellular number without heading of 08/628/+628
    if (phone.length < 8 || phone.length > 13) {
      console.log('VALIDATION FAILED: Invalid phone format:', phone);
      showCustomAlert('Error', 'Format nomor HP tidak valid');
      return false;
    }

    // Validate phone format to make sure it is valid Indonesian cellular number without heading of 08/628/+628
    if (phone.startsWith('08') || phone.startsWith('628') || phone.startsWith('+628')) {
      console.log('VALIDATION FAILED: Invalid phone format:', phone);
      showCustomAlert('Error', 'Ketikkan nomor HP setelah "08" saja');
      return false;
    }
    
    if (birthDate && !/^\d{2}-\d{2}-\d{4}$/.test(birthDate)) {
      showCustomAlert('Error', 'Format tanggal lahir harus DD-MM-YYYY');
      return false;
    }
    
    // Password validation (only if user wants to change password)
    if (newPassword || confirmNewPassword) {
      if (newPassword.length < 8) {
        showCustomAlert('Error', 'Password baru minimal 8 karakter');
        return false;
      }
      
      if (newPassword !== confirmNewPassword) {
        showCustomAlert('Error', 'Konfirmasi password baru tidak sesuai');
        return false;
      }
    }
    
    return true;
  };

  const handleUpdateProfile = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      const completePhone = `08${formData.phone}` || "";

      // Get session data
      const sessionId = await AsyncStorage.getItem('session_id');
      const rememberToken = await AsyncStorage.getItem('remember_token');
      
      if (!sessionId) {
        throw new Error('Session tidak valid. Silakan login ulang.');
      }

      const auth_token = await AsyncStorage.getItem('auth_token');
      if (!auth_token) {
        throw new Error('Session tidak valid. Silakan login ulang.');
      }

      // Prepare update data (only send changed fields)
      const updateData = new FormData();
      updateData.append('platform', 'zoolyfe club');
      updateData.append('auth_token', auth_token);
      updateData.append("name", formData.fullName);
      updateData.append("email", formData.email);
      updateData.append("telp", completePhone);
      
      // Handle password change
      if (formData.newPassword) {
        updateData.append("current_password", formData.currentPassword);
        updateData.append("new_password", formData.newPassword);
        updateData.append("confirm_password", formData.confirmNewPassword);
      }
      
      // Handle profile picture
      if (formData.profilePicture && formData.profilePicture.uri) {
        updateData.append("avatar", formData.profilePicture.uri);
      }

      console.log('Sending profile update request...');
      
      const response = await fetch(`${API_BASE_URL}/api/v1/update-profile`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          "X-Api-Key": Constants.expoConfig?.extra?.API_KEY
        },
        body: updateData
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        // Update local storage with new data
        if (result.data && result.data.user_data) {
          await AsyncStorage.setItem('userData', JSON.stringify(result.data.user_data));
        }
        
        showCustomAlert(
          'Berhasil',
          'Profil berhasil diperbarui!',
          'success'
        );
        
        // Reset password fields
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmNewPassword: ''
        }));
        
        // Update original data
        setOriginalData(formData);
        
        // Navigate back after a short delay
        setTimeout(() => {
          navigation.replace("MainTabs");
        }, 1500);
      } else {
        showCustomAlert('Error', result.message || 'Gagal memperbarui profil. Silakan coba lagi.');
      }
      
    } catch (error) {
      console.error('Profile update error:', error);
      showCustomAlert('Error', error.message || 'Gagal memperbarui profil. Silakan coba lagi.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingProfile) {
    return (
      <View style={[styles.container, styles.loadingContainer]}>
        <ActivityIndicator size="large" color="#000000" />
        <Text style={styles.loadingText}>Memuat data profil...</Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView 
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <View style={styles.container}>
        <ScrollView 
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.header}>
            <TouchableOpacity 
              style={styles.backButton}
              onPress={() => navigation.goBack()}
            >
              <Ionicons name="arrow-back" size={24} color="#000000" />
            </TouchableOpacity>
          </View>

          <View style={styles.content}>
            <View style={styles.titleSection}>
              <Text style={styles.title}>Edit Profil</Text>
              <Text style={styles.subtitle}>
                Perbarui informasi profil Anda
              </Text>
            </View>

            <View style={styles.formSection}>
              {/* Profile Picture */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Foto Profil</Text>
                <TouchableOpacity 
                  style={styles.profilePictureContainer}
                  onPress={handleImagePicker}
                >
                  {profileImageUri ? (
                    <Image source={{ uri: profileImageUri }} style={styles.profilePicture} />
                  ) : (
                    <View style={styles.profilePicturePlaceholder}>
                      <Ionicons name="person" size={40} color="#94A3B8" />
                    </View>
                  )}
                  <View style={styles.profilePictureOverlay}>
                    <Ionicons name="camera" size={20} color="#FFFFFF" />
                  </View>
                </TouchableOpacity>
                <Text style={styles.profilePictureHint}>Ketuk untuk mengubah foto profil</Text>
              </View>

              {/* Full Name */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Nama Lengkap</Text>
                <View style={styles.inputContainer}>
                  <TextInput
                    style={styles.input}
                    placeholder="Nama lengkap"
                    placeholderTextColor="#94A3B8"
                    value={formData.fullName}
                    onChangeText={(value) => handleInputChange('fullName', value)}
                    autoCapitalize="words"
                    autoCorrect={false}
                  />
                </View>
              </View>

              {/* Email */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Email *</Text>
                <View style={isEmailEditable ? styles.inputContainer : styles.inputContainerDisabled}>
                  <TextInput
                    style={styles.input}
                    placeholder="contoh@email.com"
                    placeholderTextColor="#94A3B8"
                    value={formData.email}
                    onChangeText={(value) => handleInputChange('email', value)}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    editable={isEmailEditable}
                  />
                </View>
              </View>

              {/* Phone */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>No. HP *</Text>
                <View style={styles.inputContainer}>
                  <Text style={styles.inputPrefix}>08-</Text>
                  <MaskedTextInput
                    mask="999-9999999999"
                    style={styles.input}
                    placeholder="xxx-xxxxxxx"
                    placeholderTextColor="#94A3B8"
                    value={formData.phone}
                    onChangeText={(text, rawText) => {
                      handleInputChange('phone', rawText);
                    }}
                    keyboardType="phone-pad"
                    autoCorrect={false}
                  />
                </View>
              </View>

              {/* Birth Date */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Tanggal Lahir</Text>
                <View style={styles.inputContainerDisabled}>
                  <TextInput
                    style={styles.input}
                    placeholder="DD-MM-YYYY"
                    placeholderTextColor="#94A3B8"
                    value={formData.birthDate}
                    onChangeText={(value) => handleInputChange('birthDate', value)}
                    keyboardType="numeric"
                    editable={false}
                  />
                  <Ionicons name="calendar-outline" size={20} color="#64748B" style={styles.inputIcon} />
                </View>
              </View>

              {/* Place of Birth */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>Tempat Lahir</Text>
                <View style={styles.inputContainerDisabled}>
                  <TextInput
                    style={styles.input}
                    placeholder="Tempat lahir"
                    placeholderTextColor="#94A3B8"
                    value={formData.placeOfBirth}
                    onChangeText={(value) => handleInputChange('placeOfBirth', value)}
                    autoCapitalize="words"
                    autoCorrect={false}
                    editable={false}
                  />
                </View>
              </View>

              {/* NIK */}
              <View style={styles.formGroup}>
                <Text style={styles.label}>NIK</Text>
                <View style={styles.inputContainerDisabled}>
                  <TextInput
                    style={styles.input}
                    placeholder="16 digit NIK"
                    placeholderTextColor="#94A3B8"
                    value={formData.nik}
                    onChangeText={(value) => handleInputChange('nik', value)}
                    keyboardType="numeric"
                    autoCorrect={false}
                    editable={false}
                  />
                </View>
              </View>

              {/* Password Section */}
              <View style={styles.passwordSection}>
                <Text style={styles.sectionTitle}>Ubah Password</Text>
                <Text style={styles.sectionSubtitle}>Kosongkan jika tidak ingin mengubah password. Kosongkan <Text style={{fontWeight: 700}}>Password Saat Ini</Text> jika belum pernah mengatur password.</Text>
                
                {/* Current Password */}
                <View style={styles.formGroup}>
                  <Text style={styles.label}>Password Saat Ini</Text>
                  <View style={styles.inputContainer}>
                    <TextInput
                      style={styles.input}
                      placeholder="Password saat ini"
                      placeholderTextColor="#94A3B8"
                      value={formData.currentPassword}
                      onChangeText={(value) => handleInputChange('currentPassword', value)}
                      secureTextEntry={!showCurrentPassword}
                      autoCapitalize="none"
                      autoCorrect={false}
                    />
                    <TouchableOpacity 
                      style={styles.eyeIcon}
                      onPress={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      <Ionicons 
                        name={showCurrentPassword ? "eye-outline" : "eye-off-outline"} 
                        size={20} 
                        color="#64748B" 
                      />
                    </TouchableOpacity>
                  </View>
                </View>

                {/* New Password */}
                <View style={styles.formGroup}>
                  <Text style={styles.label}>Password Baru</Text>
                  <View style={styles.inputContainer}>
                    <TextInput
                      style={styles.input}
                      placeholder="Minimal 8 karakter"
                      placeholderTextColor="#94A3B8"
                      value={formData.newPassword}
                      onChangeText={(value) => handleInputChange('newPassword', value)}
                      secureTextEntry={!showNewPassword}
                      autoCapitalize="none"
                      autoCorrect={false}
                    />
                    <TouchableOpacity 
                      style={styles.eyeIcon}
                      onPress={() => setShowNewPassword(!showNewPassword)}
                    >
                      <Ionicons 
                        name={showNewPassword ? "eye-outline" : "eye-off-outline"} 
                        size={20} 
                        color="#64748B" 
                      />
                    </TouchableOpacity>
                  </View>
                  <Text style={styles.note}>{`Minimal 8 karakter, wajib mengandung satu angka, satu huruf kapital dan satu karakter khusus`}</Text>
                </View>

                {/* Confirm New Password */}
                <View style={styles.formGroup}>
                  <Text style={styles.label}>Konfirmasi Password Baru</Text>
                  <View style={styles.inputContainer}>
                    <TextInput
                      style={styles.input}
                      placeholder="Konfirmasi password baru"
                      placeholderTextColor="#94A3B8"
                      value={formData.confirmNewPassword}
                      onChangeText={(value) => handleInputChange('confirmNewPassword', value)}
                      secureTextEntry={!showConfirmPassword}
                      autoCapitalize="none"
                      autoCorrect={false}
                    />
                    <TouchableOpacity 
                      style={styles.eyeIcon}
                      onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      <Ionicons 
                        name={showConfirmPassword ? "eye-outline" : "eye-off-outline"} 
                        size={20} 
                        color="#64748B" 
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              </View>

              {/* Update Button */}
              <TouchableOpacity 
                style={[styles.updateButton, isLoading && styles.disabledButton]}
                onPress={handleUpdateProfile}
                disabled={isLoading}
              >
                {isLoading ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <Text style={styles.updateButtonText}>Perbarui Profil</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>

        {customAlertModal}
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  loadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
    color: '#64748B',
    fontFamily: 'Poppins'
  },
  scrollContent: {
    flexGrow: 1,
    padding: 16,
    paddingTop: 32,
  },
  header: {
    marginBottom: 32,
  },
  backButton: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
  },
  titleSection: {
    marginBottom: 32,
  },
  title: {
    fontSize: 30,
    lineHeight: 36,
    fontFamily: 'Poppins-Semibold',
    fontWeight: 600,
    color: '#00000',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 24,
    fontFamily: 'Poppins',
    color: '#64748B',
  },
  formSection: {
    flex: 1,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    color: '#374151',
    marginBottom: 8,
    fontFamily: 'Poppins-Medium',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#AAAAAA',
    paddingHorizontal: 16,
    height: 48,
  },
  inputContainerDisabled: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E2E8F0',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#AAAAAA',
    paddingHorizontal: 16,
    height: 48,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#1E293B',
    fontFamily: 'Poppins',
  },
  inputPrefix: {
    fontSize: 16,
    color: '#1E293B',
    fontFamily: 'Poppins',
    // marginRight: 8,
  },
  eyeIcon: {
    padding: 4,
  },
  inputIcon: {
    marginLeft: 8,
  },
  profilePictureContainer: {
    alignSelf: 'center',
    position: 'relative',
    marginBottom: 8,
  },
  profilePicture: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: '#F1F5F9',
  },
  profilePicturePlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: '#F1F5F9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  profilePictureOverlay: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#000000',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  profilePictureHint: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
    fontFamily: 'Poppins',
    fontWeight: 400,
  },
  passwordSection: {
    marginTop: 32,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#1E293B',
    marginBottom: 4,
    fontFamily: 'Poppins-SemiBold',
  },
  sectionSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    fontWeight: 400,
    fontFamily: 'Poppins',
    color: '#64748B',
    marginBottom: 20,
  },
  updateButton: {
    backgroundColor: '#000000',
    borderRadius: 6,
    padding: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 32,
    marginBottom: 24,
  },
  disabledButton: {
    opacity: 0.7,
  },
  updateButtonText: {
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    color: '#FFFFFF',
    fontSize: 14,
    lineHeight: 20,
  },
  note: {
    fontFamily: 'Poppins',
    fontSize: 12,
    color: '#94A3B8',
    marginTop: 6
  },
});